import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowUpRight, Check, ExternalLink, Loader2, PlugZap, RefreshCcw, Zap,
} from "lucide-react";
import { api } from "@/lib/api";
import { connectApi, CONNECT_TESTIDS as T } from "@/lib/connectApi";
import MarketplaceNav from "@/components/coachly/MarketplaceNav";

// Alias for test IDs used across sub-components below.
const CONNECT_TESTIDS = T;

// ----------------------------------------------------------------------------
// Coach Connect Dashboard
// ----------------------------------------------------------------------------
// Two-column layout with:
//  1. Coach picker + "Create connected account" form.
//  2. Account status card, onboarding CTA, and product creator.
// Uses the current Coachly design language (headline serif, subdued eyebrows,
// pill CTAs) but keeps the underlying HTML deliberately simple.

export default function ConnectDashboard() {
  const [coaches, setCoaches] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  const [accountId, setAccountId] = useState(() => {
    // If Stripe onboarding redirects back with ?accountId=..., pick it up.
    const p = new URLSearchParams(window.location.search).get("accountId");
    return p || sessionStorage.getItem("coachly_connect_account") || "";
  });
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Load coach list so the platform admin can pick which coach is being onboarded.
  useEffect(() => {
    api.get("/coaches").then((r) => setCoaches(r.data.coaches || [])).catch(() => setCoaches([]));
  }, []);

  // Whenever a coach is picked, pre-fill display_name + contact_email.
  useEffect(() => {
    const c = coaches.find((x) => x.slug === selectedSlug);
    if (c) {
      setDisplayName(c.name);
      // Coach public API doesn't expose contact_email; leave it for the user
      // to type in — matches the sample's "if a value needs to be filled in"
      // behavior.
    }
  }, [selectedSlug, coaches]);

  useEffect(() => {
    if (accountId) {
      sessionStorage.setItem("coachly_connect_account", accountId);
      refreshStatus(accountId);
    }
    // eslint-disable-next-line
  }, [accountId]);

  const refreshStatus = async (id = accountId) => {
    if (!id) return;
    setStatusLoading(true);
    try {
      const s = await connectApi.accountStatus(id);
      setStatus(s);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load account status.");
      setStatus(null);
    } finally {
      setStatusLoading(false);
    }
  };

  const createAccount = async (e) => {
    e.preventDefault();
    setError("");
    if (!selectedSlug) return setError("Pick a coach first.");
    if (!displayName.trim()) return setError("Display name is required.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(contactEmail)) return setError("Please enter a valid email.");
    setBusy(true);
    try {
      const res = await connectApi.createAccount({
        coach_slug: selectedSlug,
        display_name: displayName.trim(),
        contact_email: contactEmail.trim(),
        country: "us",
      });
      setAccountId(res.account_id);
      toast.success(res.created ? "Connected account created." : "Existing account loaded.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to create account.";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const goOnboard = async () => {
    if (!accountId) return;
    setBusy(true);
    try {
      const res = await connectApi.onboardingLink(accountId);
      window.location.href = res.url; // hosted Stripe onboarding
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Onboarding link failed.");
      setBusy(false);
    }
  };

  return (
    <div data-testid={T.page} className="bg-white min-h-screen">
      <MarketplaceNav />

      <section className="border-b border-slate-100 bg-gradient-to-b from-slate-50/40 to-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="eyebrow">§ Stripe Connect · sample integration</div>
            <h1 className="mt-4 font-display font-medium text-[42px] sm:text-[60px] md:text-[76px] leading-[0.95] tracking-[-0.05em] text-slate-900 max-w-[22ch]">
              Coach payouts, <span className="italic font-light text-[#2563EB]">powered by</span> Stripe.
            </h1>
            <p className="mt-6 text-slate-600 text-lg max-w-[58ch] leading-relaxed">
              Onboard a coach as a V2 Connect account so single-session bookings pay out to their bank.
              Coachly collects a {(1000 / 100).toFixed(0)}% application fee on every session.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-12 md:py-20 grid lg:grid-cols-[420px_1fr] gap-10 lg:gap-14">
        {/* Left: Create account */}
        <div>
          <Panel step="1" title="Create a connected account" subtitle="V2 accounts.create — no top-level type">
            <form onSubmit={createAccount} className="space-y-4">
              <Field label="Coach">
                <select
                  value={selectedSlug}
                  onChange={(e) => setSelectedSlug(e.target.value)}
                  data-testid={T.coachSelect}
                  className="w-full h-11 rounded-xl bg-slate-50 border border-slate-200 px-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25"
                  required
                >
                  <option value="">Select a coach…</option>
                  {coaches.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.name} · {c.city}</option>
                  ))}
                </select>
              </Field>
              <Field label="Display name">
                <input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  data-testid={T.displayName}
                  className="w-full h-11 rounded-xl bg-slate-50 border border-slate-200 px-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25"
                  required
                />
              </Field>
              <Field label="Contact email">
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  data-testid={T.contactEmail}
                  placeholder="coach@example.com"
                  className="w-full h-11 rounded-xl bg-slate-50 border border-slate-200 px-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25"
                  required
                />
              </Field>

              <button
                type="submit"
                disabled={busy}
                data-testid={T.createBtn}
                className="w-full h-11 rounded-full bg-slate-900 hover:bg-[#2563EB] text-white text-[13.5px] font-semibold transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlugZap className="w-4 h-4" strokeWidth={2.4} />}
                Create connected account
              </button>
              {error && <div data-testid={T.errorMsg} className="text-[13px] text-red-500">{error}</div>}
            </form>
          </Panel>
        </div>

        {/* Right: Status + onboard link */}
        <div className="space-y-8">
          {accountId ? (
            <StatusCard
              status={status}
              accountId={accountId}
              loading={statusLoading}
              onRefresh={() => refreshStatus()}
              onOnboard={goOnboard}
              busy={busy}
            />
          ) : (
            <Placeholder title="No account yet" text="Create a connected account on the left. Its status will appear here." />
          )}
        </div>
      </section>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Sub-components
// ----------------------------------------------------------------------------

function Panel({ step, title, subtitle, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-7">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-slate-900 text-white grid place-items-center text-[12px] font-semibold">{step}</div>
        <div>
          <div className="font-display text-xl tracking-tight text-slate-900">{title}</div>
          {subtitle && <div className="text-[12px] text-slate-500 mt-0.5">{subtitle}</div>}
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] tracking-[0.24em] uppercase text-slate-500 font-semibold">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function StatusPill({ ok, warn, label }) {
  const cls = ok
    ? "bg-[#10B981]/10 border-[#10B981]/30 text-[#059669]"
    : warn
    ? "bg-amber-50 border-amber-200 text-amber-700"
    : "bg-slate-100 border-slate-200 text-slate-600";
  return (
    <span className={`inline-flex items-center gap-1 text-[11.5px] font-semibold px-2.5 py-1 rounded-full border ${cls}`}>
      {label}
    </span>
  );
}

function StatusCard({ status, accountId, loading, onRefresh, onOnboard, busy }) {
  const ready = status?.ready_to_receive_payments;
  const complete = status?.onboarding_complete;

  return (
    <Panel step="2" title="Account status" subtitle="Read live from Stripe on every refresh — never cached.">
      <div data-testid={CONNECT_TESTIDS.statusBlock} className="grid md:grid-cols-2 gap-6">
        <div className="min-w-0">
          <div className="text-[11px] tracking-[0.24em] uppercase text-slate-500 font-semibold">Account ID</div>
          <div data-testid={CONNECT_TESTIDS.accountId} className="mt-1 font-mono text-[13px] break-all">{accountId}</div>
          {status && (
            <>
              <div className="mt-4 text-[13.5px] text-slate-700">
                <div><span className="text-slate-400">Display name:</span> {status.display_name}</div>
                <div><span className="text-slate-400">Contact:</span> {status.contact_email}</div>
                <div><span className="text-slate-400">Dashboard:</span> {status.dashboard}</div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <StatusPill ok={ready} label={`Transfers: ${status.transfers_status || "unknown"}`} />
                <StatusPill ok={complete} warn={!complete} label={`Requirements: ${status.requirements_status || "n/a"}`} />
                {ready && (
                  <StatusPill ok label={<span data-testid={CONNECT_TESTIDS.readyBadge}>Ready to collect</span>} />
                )}
              </div>
            </>
          )}
        </div>
        <div className="flex flex-col gap-3">
          <button
            onClick={onOnboard}
            disabled={busy}
            data-testid={CONNECT_TESTIDS.onboardBtn}
            className="h-11 rounded-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white text-[13.5px] font-semibold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            <Zap className="w-4 h-4" strokeWidth={2.4} />
            Onboard to collect payments
            <ArrowUpRight className="w-4 h-4" strokeWidth={2.4} />
          </button>
          <button
            onClick={onRefresh}
            disabled={loading}
            data-testid={CONNECT_TESTIDS.refreshStatusBtn}
            className="h-11 rounded-full border border-slate-200 text-slate-700 hover:border-slate-300 text-[13.5px] font-semibold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            Refresh status
          </button>
          <p className="text-[11.5px] text-slate-400 leading-relaxed">
            Onboarding opens Stripe's hosted flow. Stripe returns to <code>/connect?accountId=…</code>.
          </p>
        </div>
      </div>
    </Panel>
  );
}

function Placeholder({ title, text }) {
  return (
    <div className="border border-dashed border-slate-200 rounded-2xl p-10 text-center text-slate-500">
      <div className="eyebrow">§ Waiting</div>
      <h3 className="mt-3 font-display text-2xl text-slate-900">{title}</h3>
      <p className="mt-1 text-[13px]">{text}</p>
    </div>
  );
}

// end
